import { t } from '../../services/HelperService';

/**
 * The client half of the commissionRule session's artifact contract (server:
 * CommissionAuthoringArtifacts, C#): output names, the JSON shape of the rule and
 * value artifacts, the host-context payload the editor sends, and the merge of a
 * finished session's outputs back into the editor's rule/value lists. Shared by the
 * preview (CommissionRulePreview) and the host (EditCommissionsView) so both read the
 * artifacts identically.
 */

const safeString = (v) => (v ?? '').toString();

export const COMMISSION_RULE_SHAPE = 'rule';
export const COMMISSION_VALUE_SHAPE_PREFIX = 'value:';

export const isCommissionRuleShape = (name) => safeString(name).trim().toLowerCase() === COMMISSION_RULE_SHAPE;
export const isCommissionValueShape = (name) => safeString(name).trim().toLowerCase().startsWith(COMMISSION_VALUE_SHAPE_PREFIX);
export const commissionValueNameFromShape = (name) => (
  isCommissionValueShape(name) ? safeString(name).trim().slice(COMMISSION_VALUE_SHAPE_PREFIX.length).trim() : ''
);

const readKey = (obj, key) => {
  if (!obj || typeof obj !== 'object') return undefined;
  if (key in obj) return obj[key];
  const lower = key.toLowerCase();
  const found = Object.keys(obj).find((k) => k.toLowerCase() === lower);
  return found === undefined ? undefined : obj[found];
};

const toBool = (v) => (typeof v === 'boolean' ? v : safeString(v).trim().toLowerCase() === 'true');
const toIntOr = (v, fallback) => {
  const n = Number.parseInt(safeString(v), 10);
  return Number.isFinite(n) ? n : fallback;
};
const nullIfEmpty = (v) => {
  const s = safeString(v).trim();
  return s ? s : null;
};

const parseObject = (json) => {
  try {
    const parsed = JSON.parse(safeString(json));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** The filter as the editor holds it: an object (parsed) or null. Strings are parsed. */
const normalizeFilter = (filter) => {
  if (filter == null) return null;
  if (typeof filter === 'string') return parseObject(filter);
  return typeof filter === 'object' && !Array.isArray(filter) ? filter : null;
};

/** Parses a "rule" output's JSON into the editor's rule shape (PascalCase), or null. */
export const parseCommissionRuleArtifact = (json) => {
  const obj = typeof json === 'object' && json ? json : parseObject(json);
  if (!obj) return null;
  return {
    Name: safeString(readKey(obj, 'name')).trim(),
    Expression: safeString(readKey(obj, 'expression')),
    CommissionDateNo: toIntOr(readKey(obj, 'commissionDateNo'), 1),
    ByJob: toBool(readKey(obj, 'byJob')),
    TextValues: nullIfEmpty(readKey(obj, 'textValues')),
  };
};

/** Parses a "value:<Name>" output's JSON into the editor's value shape (PascalCase), or null. */
export const parseCommissionValueArtifact = (json) => {
  const obj = typeof json === 'object' && json ? json : parseObject(json);
  if (!obj) return null;
  const sortOrderRaw = readKey(obj, 'sortOrder');
  return {
    Name: safeString(readKey(obj, 'name')).trim(),
    Expression: safeString(readKey(obj, 'expression')),
    Filter: normalizeFilter(readKey(obj, 'filter')),
    GroupBy: nullIfEmpty(readKey(obj, 'groupBy')),
    SummaryBy: nullIfEmpty(readKey(obj, 'summaryBy')),
    SummaryType: safeString(readKey(obj, 'summaryType')).trim(),
    ShowOnStatement: toBool(readKey(obj, 'showOnStatement')),
    IsText: toBool(readKey(obj, 'isText')),
    SortOrder: sortOrderRaw == null || safeString(sortOrderRaw).trim() === '' ? null : toIntOr(sortOrderRaw, null),
  };
};

/** Editor rule -> the camelCase host-context / artifact shape (never Users). */
export const ruleToArtifact = (rule) => ({
  name: safeString(rule?.Name).trim(),
  expression: safeString(rule?.Expression),
  commissionDateNo: toIntOr(rule?.CommissionDateNo, 1),
  byJob: !!rule?.ByJob,
  textValues: nullIfEmpty(rule?.TextValues),
});

/** Editor value -> the camelCase host-context / artifact shape. */
export const valueToArtifact = (value) => ({
  name: safeString(value?.Name).trim(),
  expression: safeString(value?.Expression),
  filter: normalizeFilter(value?.Filter),
  groupBy: nullIfEmpty(value?.GroupBy),
  summaryBy: nullIfEmpty(value?.SummaryBy),
  summaryType: safeString(value?.SummaryType).trim(),
  showOnStatement: !!value?.ShowOnStatement,
  isText: !!value?.IsText,
  sortOrder: value?.SortOrder == null ? null : toIntOr(value.SortOrder, null),
});

/**
 * The host context the commissions editor sends with every chat turn: the focused
 * rule's name, EVERY rule and value as the editor currently holds them (unsaved edits
 * included, identity by name, never Users), the selectable commission dates and an
 * optional id -> name legend the client already knows (the server adds its own).
 */
export const buildCommissionHostContext = ({ focusedRule, rules, values, commissionDateOptions, legend }) => ({
  focusedRule: safeString(focusedRule?.Name).trim(),
  rules: (Array.isArray(rules) ? rules : []).map(ruleToArtifact).filter((r) => r.name),
  values: (Array.isArray(values) ? values : [])
    .slice()
    .sort((a, b) => (Number(a?.SortOrder) || 0) - (Number(b?.SortOrder) || 0))
    .map(valueToArtifact)
    .filter((v) => v.name),
  commissionDateOptions: (Array.isArray(commissionDateOptions) ? commissionDateOptions : [])
    .map((o) => ({ no: toIntOr(o?.ID, 0), label: safeString(o?.Description) }))
    .filter((o) => o.no > 0),
  ...(legend && typeof legend === 'object' && Object.keys(legend).length > 0 ? { legend } : {}),
});

/**
 * Reads a session's outputs ([{ name, script }]) into { rule, values, invalid }:
 * the parsed rule artifact (or null when absent/unparseable), the parsed value
 * artifacts (keyed by their output name's value part) and the names of outputs that
 * could not be read.
 */
export const readCommissionOutputs = (outputs) => {
  let rule = null;
  const values = [];
  const invalid = [];
  for (const output of (Array.isArray(outputs) ? outputs : [])) {
    const name = safeString(output?.name).trim();
    const script = safeString(output?.script).trim();
    if (!name || !script) continue;
    if (isCommissionRuleShape(name)) {
      const parsed = parseCommissionRuleArtifact(script);
      if (parsed) rule = parsed; else invalid.push(name);
    } else if (isCommissionValueShape(name)) {
      const parsed = parseCommissionValueArtifact(script);
      const shapeValueName = commissionValueNameFromShape(name);
      if (parsed) {
        // The output name is the identity; a JSON "name" that disagrees is corrected
        // to it (the server flags the mismatch during the session).
        if (!parsed.Name) parsed.Name = shapeValueName;
        values.push(parsed);
      } else {
        invalid.push(name);
      }
    }
  }
  return { rule, values, invalid };
};

const sameName = (a, b) => safeString(a).trim().toLowerCase() === safeString(b).trim().toLowerCase();

/**
 * Applies a finished session's outputs to the editor's lists. The focused rule (by
 * id) takes the rule artifact's fields (Users untouched); each value artifact replaces
 * the same-named value's authored fields (ID, SortOrder kept) or is appended as a new
 * value (negative temp id, SortOrder after the last). Returns the next lists plus a
 * summary of what changed; nothing is ever removed.
 */
export const applyCommissionOutputs = ({ outputs, rules, values, focusedRuleID, nextTempId }) => {
  const { rule, values: valueArtifacts, invalid } = readCommissionOutputs(outputs);
  let nextRules = Array.isArray(rules) ? rules : [];
  let nextValues = Array.isArray(values) ? values : [];
  let ruleChanged = false;
  const added = [];
  const changed = [];

  if (rule) {
    nextRules = nextRules.map((r) => {
      if (r.ID !== focusedRuleID) return r;
      const merged = {
        ...r,
        Name: rule.Name || r.Name,
        Expression: rule.Expression,
        CommissionDateNo: rule.CommissionDateNo,
        ByJob: rule.ByJob,
        TextValues: rule.TextValues,
      };
      ruleChanged = JSON.stringify(ruleToArtifact(merged)) !== JSON.stringify(ruleToArtifact(r));
      return merged;
    });
  }

  let tempId = typeof nextTempId === 'function' ? null : nextTempId;
  const takeTempId = () => (typeof nextTempId === 'function' ? nextTempId() : tempId--);
  let maxSort = nextValues.reduce((m, v) => Math.max(m, Number(v?.SortOrder) || 0), -1);

  for (const artifact of valueArtifacts) {
    if (!artifact.Name) continue;
    const ix = nextValues.findIndex((v) => sameName(v.Name, artifact.Name));
    if (ix >= 0) {
      const existing = nextValues[ix];
      const merged = {
        ...existing,
        Expression: artifact.Expression,
        Filter: artifact.Filter,
        GroupBy: artifact.GroupBy,
        SummaryBy: artifact.SummaryBy,
        SummaryType: artifact.SummaryType,
        ShowOnStatement: artifact.ShowOnStatement,
        IsText: artifact.IsText,
      };
      if (JSON.stringify(valueToArtifact(merged)) !== JSON.stringify(valueToArtifact(existing))) {
        nextValues = nextValues.map((v, i) => (i === ix ? merged : v));
        changed.push(artifact.Name);
      }
    } else {
      maxSort += 1;
      nextValues = [...nextValues, {
        ID: takeTempId(),
        Name: artifact.Name,
        Expression: artifact.Expression,
        Filter: artifact.Filter,
        GroupBy: artifact.GroupBy,
        SummaryBy: artifact.SummaryBy,
        SummaryType: artifact.SummaryType,
        ShowOnStatement: artifact.ShowOnStatement,
        IsText: artifact.IsText,
        SortOrder: artifact.SortOrder ?? maxSort,
      }];
      added.push(artifact.Name);
    }
  }

  return { rules: nextRules, values: nextValues, ruleChanged, added, changed, invalid };
};

// ---- Filter description (human-readable) ----

const OP_LABEL_KEYS = {
  eq: 'eq', ne: 'ne', gt: 'gt', ge: 'ge', lt: 'lt', le: 'le',
  startswith: 'startswith', endswith: 'endswith', isempty: 'isempty', isnotempty: 'isnotempty',
  in: 'in', notin: 'notin', within: 'within', relative: 'relative',
  anygt: 'anygt', anyge: 'anyge', anylt: 'anylt', anyle: 'anyle',
  allin: 'allin', allgt: 'allgt', allge: 'allge', alllt: 'alllt', allle: 'allle', nonein: 'nonein',
};

const LOGIC_LABELS = { and: 'AND', or: 'OR', notand: 'NOT AND', notor: 'NOT OR' };

const fieldLabel = (fieldId) => {
  const id = safeString(fieldId).trim();
  const dot = id.indexOf('.');
  if (dot < 0) return id;
  const entity = id.slice(0, dot);
  const field = id.slice(dot + 1);
  const camel = entity ? entity[0].toLowerCase() + entity.slice(1) : entity;
  const key = `application_strings.entities.${camel}.${field}`;
  const translated = t(key);
  return translated && translated !== key ? translated : field;
};

const describeValue = (value, resolve) => {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map((v) => describeValue(v, resolve)).join(', ');
  if (typeof value === 'object') {
    if (Array.isArray(value.stages)) return describeValue(value.stages, resolve);
    try { return JSON.stringify(value); } catch { return safeString(value); }
  }
  const raw = safeString(value);
  const resolved = resolve ? resolve(raw) : null;
  if (resolved) return resolved;
  const translated = t(raw);
  return translated && translated !== raw ? translated : raw;
};

/**
 * Describes a Kendo-style filter group ({ logic, filter: [rule | group] }) as nested
 * lines: each rule as "Field  Operator  value(s)", each group indented under its logic
 * label. `resolve(id)` may map a tenant id to a display name (legend/lookup catalog);
 * picklist codes fall back to their translation. Returns [] for no filter.
 */
export const describeCommissionFilter = (filter, resolve) => {
  const root = normalizeFilter(filter);
  if (!root) return [];
  const lines = [];
  const walk = (group, depth) => {
    const logic = safeString(group?.logic).trim().toLowerCase();
    const rules = Array.isArray(group?.filter) ? group.filter : [];
    if (depth > 0 || rules.length > 1 || logic.startsWith('not')) {
      lines.push({ depth, text: LOGIC_LABELS[logic] ?? (logic.toUpperCase() || 'AND'), kind: 'logic' });
    }
    const childDepth = depth > 0 || rules.length > 1 || logic.startsWith('not') ? depth + 1 : depth;
    for (const item of rules) {
      if (!item || typeof item !== 'object') continue;
      if (Array.isArray(item.filter)) {
        walk(item, childDepth);
        continue;
      }
      const op = safeString(item.op).trim().toLowerCase();
      const opKey = OP_LABEL_KEYS[op];
      const opLabel = opKey ? t(`application_strings.operators.${opKey}`) : op;
      const needsValue = op !== 'isempty' && op !== 'isnotempty';
      lines.push({
        depth: childDepth,
        kind: 'rule',
        field: fieldLabel(item.field),
        op: opLabel,
        value: needsValue ? describeValue(item.value, resolve) : '',
      });
    }
  };
  walk(root, 0);
  return lines;
};
